import {
  Badge,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import React, { useEffect, useState } from "react"
import { useThemeContext } from "contexts/ThemeContext"
import { useTranslation } from "react-i18next"
import ArrowRightIcon from "@mui/icons-material/ArrowRight"
import { styles } from "./styles/NotificationsManu.styles"
import NotificationItem from "./NotificationItem"
import useNotificationApi from "modules/notifications"
import Image from "components/Image"
import TeacherMessage from "components/Tools/TeacherMessage"
import MessageDetailModal from "./MessageDetailModal"
import MessageAnnouncementModal from "./MessageAnnouncementModal"
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff"
import { useAuthContext } from "contexts/AuthContext"

export default function TeacherNotificationsMenu({
  sx = {},
  total = 0,
  notificationLastUpdate = "",
}) {
  const {
    getTeacherMessages,
    isFetching,
    setNotificationSeenInformative,
    getTeacherAnnouncement,
  } = useNotificationApi()
  const { t } = useTranslation()
  const { bgColor } = useThemeContext()
  const [anchorEl, setAnchorEl] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [openTeacherMessages, setOpenTeacherMessages] = useState(false)
  const [shouldTakeScreenshot, setShouldTakeScreenshot] = useState(false)
  const [openMessageReport, setOpenMessageReport] = useState(null)
  const [totalNotifications, setTotalNotifications] = useState(total)
  const [openAnnouncement, setOpenAnnouncement] = useState(null)
  const { selectedGroupData } = useAuthContext()
  const teacher =
    selectedGroupData?.teachers?.find((teacher) => teacher.isMainTeacher) ||
    null
  const open = Boolean(anchorEl)
  const theme = useTheme()

  const handleClick = (event) => {
    setNotificationSeenInformative()
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (open) {
      loadNotifications()
    }
  }, [open])

  const loadNotifications = async () => {
    const response = await getTeacherMessages()
    if (response) {
      setNotifications(response)
    }
  }

  useEffect(() => {
    setTotalNotifications(total)
  }, [total, notificationLastUpdate])

  const onNotificationOpened = async (messageData) => {
    const { referenceId, messageTypeId } = messageData
    console.log("Open notification", referenceId)
    // update notifications state
    setNotifications((prevNotifications) =>
      prevNotifications.map((notification) =>
        notification.referenceId === referenceId
          ? { ...notification, hasUnreadMessages: false }
          : notification
      )
    )
    setTotalNotifications((prevTotal) => Math.max(0, prevTotal - 1))
    setAnchorEl(null)
    if (messageTypeId === 1) {
      setOpenMessageReport(messageData)
      setShouldTakeScreenshot(false)
    } else {
      console.log("Open announcement", messageData)
      const response = await getTeacherAnnouncement({ referenceId })
      if (response) {
        const files = []
        if (
          response?.audioFile !== null &&
          response?.audioFile !== undefined &&
          response?.audioFile !== ""
        ) {
          files.push({
            fileTypeId: 1,
            type: "audio",
            fileURL: response.audioFile,
          })
        }
        if (
          response?.imageFile !== null &&
          response?.imageFile !== undefined &&
          response?.imageFile !== ""
        ) {
          files.push({
            fileTypeId: 2,
            type: "image",
            fileURL: response.imageFile,
          })
        }
        setOpenAnnouncement({
          ...messageData,
          files,
          messagePreview: response.content || messageData.messagePreview,
        })
      } else {
        setOpenAnnouncement(messageData)
      }
    }
  }

  const openTeacherMessage = () => {
    setAnchorEl(null)
    setOpenTeacherMessages(true)
    setShouldTakeScreenshot(true)
  }

  const onTeacherMessageClosed = (shouldTakeScreenshot = false) => {
    setOpenTeacherMessages(false)
  }

  return (
    <React.Fragment>
      <TeacherMessage
        open={openTeacherMessages}
        onClose={onTeacherMessageClosed}
        takeScreenshot={shouldTakeScreenshot}
      />
      <MessageDetailModal
        visible={openMessageReport !== null}
        onClose={() => setOpenMessageReport(null)}
        reportId={openMessageReport?.referenceId}
        {...openMessageReport}
      />
      <MessageAnnouncementModal
        open={openAnnouncement !== null}
        onClose={() => setOpenAnnouncement(null)}
        {...openAnnouncement}
      />
      <IconButton
        size="large"
        edge="start"
        color="inherit"
        id={`notification-teacherMessages-btn`}
        aria-controls={open ? "notification-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={sx}
      >
        <Badge
          badgeContent={totalNotifications}
          color="danger"
          sx={styles.notificationBadge}
        >
          <Image
            key={totalNotifications > 0 ? "chat-on" : "chat-off"}
            name={totalNotifications > 0 ? "chat-on" : "chat-off"}
            width={"30px"}
            height={"30px"}
          />
        </Badge>
      </IconButton>
      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": `notification-teacherMessages-btn`,
          },
          paper: {
            // elevation: 0,
            sx: {
              minWidth: 250,
              maxWidth: 400,
              width: "calc(100vw - 100px)",
              border: `1px solid ${theme.palette.grey.lighter2}`,
              p: ".833em 1em",
              color: "rgba(0,0,0,.87)",
              boxShadow:
                "0 2px 4px 0 rgba(34,36,38,.12),0 2px 10px 0 rgba(34,36,38,.15)",
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .notification-btn": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: bgColor,
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Stack direction={"row"} alignItems={"center"}>
          <Typography
            variant="span"
            fontWeight={"bold"}
            color={theme.palette.common.black}
          >
            {t("notifications")}
          </Typography>
          <ArrowRightIcon
            fontSize="small"
            sx={{ color: theme.palette.common.black }}
          />
          <Typography
            variant="body1"
            fontWeight={"bold"}
            sx={styles.notificationHeaderText}
          >
            {t("teacher_messages")}
          </Typography>
        </Stack>
        {
          <Stack
            sx={[
              styles.notifications,
              notifications.length === 0 && { justifyContent: "safe center" },
            ]}
          >
            {isFetching && notifications.length === 0 && (
              <CircularProgress
                sx={{
                  color: theme.palette.primary.main,
                  width: 50,
                  height: 50,
                  alignSelf: "center",
                }}
                size={"large"}
              />
            )}
            {!isFetching && notifications.length === 0 && (
              <Stack alignItems={"center"} alignSelf={"center"}>
                <NotificationsOffIcon
                  fontSize="large"
                  sx={{ fontSize: 100, color: "#84818a" }}
                />
                <Typography color={"#84818a"}>
                  {t("there_arent_any_notifications")}
                </Typography>
              </Stack>
            )}
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.notificationId}
                onNotificationOpened={(evt) => {
                  evt.stopPropagation()
                  onNotificationOpened({ ...notification })
                }}
                {...notification}
                avatar={notification.icon}
                content={notification.messagePreview}
              />
            ))}
          </Stack>
        }
        {teacher?.acceptDirectMessages && (
          <Stack sx={styles.notificationFooter}>
            <Button
              variant="contained"
              disableElevation
              onClick={openTeacherMessage}
              sx={styles.teacherMessagesBtn}
            >
              {t("send_new_message")}
            </Button>
          </Stack>
        )}
      </Menu>
    </React.Fragment>
  )
}
